import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Cpu, HardDrive, MemoryStick, Activity, RefreshCw, Server, 
  Clock, AlertTriangle, CheckCircle, XCircle, ListChecks, Timer
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColor = (s) => {
  switch (s) {
    case 'ok': return 'bg-green-500';
    case 'warn': return 'bg-yellow-500';
    case 'fail': return 'bg-red-500';
    default: return 'bg-gray-400';
  }
};
const statusLabel = (s) => {
  switch (s) {
    case 'ok': return 'OK';
    case 'warn': return 'WARN';
    case 'fail': return 'FAIL';
    default: return 'UNKNOWN';
  }
};

const CronJobsPanel = ({ data, loading, onRefresh }) => {
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Memuat status cron...
      </div>
    );
  }
  if (!data?.jobs?.length) {
    return <div className="text-gray-500 py-8 text-center">Belum ada data cron.</div>;
  }
  const s = data.summary || {};
  return (
    <div>
      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border dark:border-slate-700">
          <ListChecks className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-gray-500 dark:text-slate-400">Total:</span>
          <span className="font-semibold">{s.total || 0}</span>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border dark:border-slate-700">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-500 dark:text-slate-400">OK:</span>
          <span className="font-semibold text-green-600">{s.ok || 0}</span>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border dark:border-slate-700">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
          <span className="text-sm text-gray-500 dark:text-slate-400">WARN:</span>
          <span className="font-semibold text-yellow-600">{s.warn || 0}</span>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border dark:border-slate-700">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
          <span className="text-sm text-gray-500 dark:text-slate-400">FAIL:</span>
          <span className="font-semibold text-red-600">{s.fail || 0}</span>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border dark:border-slate-700">
          <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
          <span className="text-sm text-gray-500 dark:text-slate-400">Unknown:</span>
          <span className="font-semibold">{s.unknown || 0}</span>
        </div>
        <div className="ml-auto text-xs text-gray-500 dark:text-slate-400 self-center">
          Update: {s.checked_at || '-'} · auto-refresh 30s
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} data-testid="cron-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-slate-900 text-gray-600 dark:text-slate-300">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Nama</th>
              <th className="text-left px-3 py-2 font-medium">Jadwal</th>
              <th className="text-left px-3 py-2 font-medium">Deskripsi</th>
              <th className="text-left px-3 py-2 font-medium">Terakhir Jalan</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Log Tail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
            {data.jobs.map((j) => (
              <tr key={j.name} className="hover:bg-gray-50 dark:hover:bg-slate-900/50" data-testid={`cron-row-${j.name}`}>
                <td className="px-3 py-2.5 font-mono text-xs font-semibold">{j.name}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-slate-300">
                    <Timer className="w-3 h-3" /> {j.schedule}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-slate-400 max-w-[220px]">{j.description}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                  <div>{j.last_run_human}</div>
                  <div className="text-gray-400">{j.last_run || '(no log)'}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white text-[10px] font-bold ${statusColor(j.status)}`} data-testid={`cron-status-${j.name}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                    {statusLabel(j.status)}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {j.log_tail?.length ? (
                    <div className="font-mono text-[10px] text-gray-500 dark:text-slate-400 max-w-[420px] max-h-16 overflow-y-auto whitespace-pre-line break-all">
                      {j.log_tail.join('\n')}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">no log</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-slate-300">
        <span>Rule status:</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500 mr-1"></span>OK = terakhir jalan &lt;2× interval</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500 mr-1"></span>WARN = 2×–4× interval</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-1"></span>FAIL = &gt;4× interval (perlu cek)</span>
      </div>
    </div>
  );
};

const SystemMonitor = () => {
  const [acsData, setAcsData] = useState(null);
  const [radiusData, setRadiusData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  // 2026-07-10 Cron Jobs Monitor state
  const [cronData, setCronData] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('resources');

  const fetchCron = async () => {
    setCronLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API_URL}/api/system/cron-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronData(res.data);
    } catch (e) {
      toast.error('Gagal mengambil status cron');
    } finally {
      setCronLoading(false);
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      // Fetch ACS and FreeRADIUS data in parallel
      const [acsRes, radiusRes] = await Promise.all([
        axios.get(`${API_URL}/api/system/remote-resources/acs`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(e => ({ data: { success: false, error: e.message } })),
        axios.get(`${API_URL}/api/system/remote-resources/freeradius`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(e => ({ data: { success: false, error: e.message } }))
      ]);
      
      setAcsData(acsRes.data);
      setRadiusData(radiusRes.data);
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Gagal mengambil data resource');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    if (activeTab === 'cron') {
      fetchCron();
      const iv = setInterval(fetchCron, 30000);
      return () => clearInterval(iv);
    }
  }, [activeTab]);

  const getStatusColor = (percent) => {
    if (percent < 50) return 'bg-green-500';
    if (percent < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusIcon = (percent) => {
    if (percent < 50) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (percent < 80) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const formatUptime = (hours) => {
    if (!hours) return '-';
    if (hours < 24) return `${hours.toFixed(1)} jam`;
    const days = Math.floor(hours / 24);
    const remainingHours = (hours % 24).toFixed(0);
    return `${days} hari ${remainingHours} jam`;
  };

  const ResourceCard = ({ serverName, serverIP, data, error }) => {
    if (error) {
      return (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Server className="w-5 h-5 text-red-500" />
              {serverName}
              {serverIP && <span className="text-xs font-normal text-gray-500 dark:text-slate-400">({serverIP})</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <XCircle className="w-5 h-5" />
              <span>Tidak dapat terhubung: {error}</span>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data) {
      return (
        <Card className="border-gray-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Server className="w-5 h-5 text-gray-400" />
              {serverName}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-gray-200 dark:border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-500" />
            {serverName}
            {serverIP && <span className="text-xs font-normal text-gray-500 dark:text-slate-400">({serverIP})</span>}
            {data.uptime_hours && (
              <span className="text-xs font-normal text-gray-500 ml-auto flex items-center gap-1 dark:text-slate-400">
                <Clock className="w-3 h-3" />
                Uptime: {formatUptime(data.uptime_hours)}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* CPU */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">CPU</span>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(data.cpu?.percent || 0)}
                <span className="text-sm font-bold">{data.cpu?.percent?.toFixed(1) || 0}%</span>
              </div>
            </div>
            <Progress value={data.cpu?.percent || 0} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
              <span>Cores: {data.cpu?.count || data.cpu?.cores || '-'}</span>
            </div>
          </div>

          {/* Memory */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MemoryStick className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium">Memory</span>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(data.memory?.percent || 0)}
                <span className="text-sm font-bold">{data.memory?.percent?.toFixed(1) || 0}%</span>
              </div>
            </div>
            <Progress value={data.memory?.percent || 0} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
              <span>Used: {data.memory?.used_gb?.toFixed(1) || 0} GB</span>
              <span>Free: {((data.memory?.total_gb || 0) - (data.memory?.used_gb || 0)).toFixed(1)} GB</span>
              <span>Total: {data.memory?.total_gb?.toFixed(1) || 0} GB</span>
            </div>
          </div>

          {/* Disk */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium">Disk</span>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(data.disk?.percent || 0)}
                <span className="text-sm font-bold">{data.disk?.percent?.toFixed(1) || 0}%</span>
              </div>
            </div>
            <Progress value={data.disk?.percent || 0} className="h-2" />
            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
              <span>Used: {data.disk?.used_gb?.toFixed(1) || 0} GB</span>
              <span>Free: {((data.disk?.total_gb || 0) - (data.disk?.used_gb || 0)).toFixed(1)} GB</span>
              <span>Total: {data.disk?.total_gb?.toFixed(1) || 0} GB</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 dark:text-slate-200">
            <Activity className="w-6 h-6 text-blue-500" />
            System Monitor
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Monitor resource usage untuk ACS Server dan FreeRADIUS Server
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-xs text-gray-500 dark:text-slate-400">
              Last update: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <Button 
            onClick={fetchResources} 
            disabled={loading}
            className="gap-2"
            data-testid="refresh-resources-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Tabs: Resources | Cron Jobs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="resources" data-testid="tab-resources">
            <Activity className="w-4 h-4 mr-2" /> Resources
          </TabsTrigger>
          <TabsTrigger value="cron" data-testid="tab-cron">
            <ListChecks className="w-4 h-4 mr-2" /> Cron Jobs
            {cronData?.summary?.fail > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                {cronData.summary.fail}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resources">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResourceCard 
              serverName="1. ACS Server (GenieACS)"
              serverIP={acsData?.host || '103.179.252.243'}
              data={acsData?.data}
              error={acsData?.success === false ? acsData.error : null}
            />
            <ResourceCard 
              serverName="2. FreeRADIUS Server"
              serverIP={radiusData?.host || '103.179.252.149'}
              data={radiusData?.data}
              error={radiusData?.success === false ? radiusData.error : null}
            />
          </div>

          <div className="mt-6 flex items-center gap-6 text-sm text-gray-600 dark:text-slate-300">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Normal (&lt;50%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>Warning (50-80%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>Critical (&gt;80%)</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cron">
          <CronJobsPanel data={cronData} loading={cronLoading} onRefresh={fetchCron} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemMonitor;
